import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus, X, Trash, TrashSimple, Copy, MarkdownLogo, LockKey, CaretDown, CaretUp,
  FolderPlus, BookOpen, IdentificationCard, Trophy, Translate, HeartStraight,
  Briefcase, Star,
} from "@phosphor-icons/react";
import { copyText, copyAsMarkdown, asMarkdown } from "@/lib/clipboard";
import { schemaFor, extractFormValues, toApiPayload } from "@/lib/customFieldSchemas";

// Quick-start tiles. Each tile creates a section with a specific `kind`,
// so the item form is tailored automatically. "custom" is a free-form fallback.
const PROMPTS = [
  { kind: "project",       icon: BookOpen,           name: "Portfolios",                hint: "personal projects, code samples, design work — drop the links." },
  { kind: "certification", icon: IdentificationCard, name: "Professional registrations", hint: "GMC, NMC, SRA, ACCA, etc. — number, body, expiry." },
  { kind: "award",         icon: Trophy,             name: "Awards",                    hint: "scholarships, competition wins, hackathon prizes — keep it short." },
  { kind: "publication",   icon: BookOpen,           name: "Publications",              hint: "papers, articles, talks. title, venue, year, link." },
  { kind: "language",      icon: Translate,          name: "Languages",                 hint: "language + proficiency (basic / conversational / fluent / native)." },
  { kind: "volunteer",     icon: HeartStraight,      name: "Volunteer work",            hint: "org, role, dates, what you did." },
  { kind: "project",       icon: Briefcase,          name: "Side projects",             hint: "freelance gigs, indie hustles, things you built for fun." },
  { kind: "certification", icon: Star,               name: "Other certificates",        hint: "anything not a degree or apprenticeship — Coursera, AWS, first aid…" },
];

// ---------- Debounce helper (mirrors Work/Ed) ----------
function useDebounced(fn, delay = 700) {
  const ref = useRef(fn);
  const timer = useRef(null);
  useEffect(() => { ref.current = fn; }, [fn]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const debounced = (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(...args), delay);
  };
  debounced.cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return debounced;
}

// ---------- One item card — always inline-editable ----------
function ItemCard({ sectionId, kind, item, onChange, onDelete, collapsed, onCollapseToggle }) {
  const sc = schemaFor(kind);
  const [values, setValues] = useState(() => extractFormValues(item, kind));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Re-hydrate from server when the item id changes (rare — pagination edge case)
  useEffect(() => { setValues(extractFormValues(item, kind)); }, [item.id, kind]);

  const persist = useDebounced(async (nextValues) => {
    setSaving(true);
    try {
      const payload = toApiPayload(nextValues, kind, sectionId);
      const { data } = await api.put(`/custom/items/${item.id}`, payload);
      onChange?.(data);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch {
      toast.error("Couldn't save changes");
    } finally { setSaving(false); }
  }, 700);

  const lockIn = async () => {
    // Required-field check
    const missing = sc.fields.find((f) => f.required && !(values[f.key] || "").toString().trim());
    if (missing) return toast.error(`${missing.label} is required`);
    persist.cancel?.();
    setSaving(true);
    try {
      const payload = toApiPayload(values, kind, sectionId);
      const { data } = await api.put(`/custom/items/${item.id}`, payload);
      onChange?.(data);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      toast.success("Locked into the vault");
      onCollapseToggle?.(true);
    } catch {
      toast.error("Couldn't lock it in — try again");
    } finally { setSaving(false); }
  };

  const setField = (key, val) => {
    const next = { ...values, [key]: val };
    setValues(next);
    setDirty(true);
    persist(next);
  };

  // Summary line for collapsed view
  const displayTitle = sc.legacy ? (item.title || "Untitled") : (sc.titleFrom(values) || "Untitled");
  const meta = sc.metaLine ? sc.metaLine(values) : "";
  const urlKey = sc.legacy ? "url" : (sc.fields.find((f) => f.type === "url")?.key || null);
  const url = urlKey ? (values[urlKey] || "") : "";
  const descKey = sc.legacy ? "body" : (sc.fields.find((f) => f.type === "textarea")?.key || null);
  const description = descKey ? (values[descKey] || "") : "";
  const copyPayload = [displayTitle, meta, url, description].filter(Boolean).join("\n");

  return (
    <li data-testid={`custom-item-${item.id}`}
      className="border-[1.5px] border-[var(--brand-ink)] rounded-md bg-white">
      {/* Header — always visible; click to expand/collapse */}
      <div className="flex items-start justify-between p-4 gap-3">
        <button onClick={() => onCollapseToggle?.(!collapsed)}
          data-testid={`toggle-custom-${item.id}`}
          className="flex-1 text-left min-w-0">
          <p className="font-medium flex items-center gap-2 flex-wrap">
            <span className="truncate">{displayTitle}</span>
            {saving && <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">saving…</span>}
            {justSaved && !saving && <span className="font-mono-d text-[10px] text-[color:var(--matcha-strong,#557755)]">saved ✓</span>}
            {dirty && !saving && !justSaved && <span className="font-mono-d text-[10px] text-[color:var(--brand-primary)]">unsaved</span>}
          </p>
          {meta && <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-1">{meta}</p>}
        </button>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => copyText(copyPayload)} title="copy entry" data-testid={`copy-custom-${item.id}`}
            className="p-1.5 hover:bg-[var(--pastel-yellow)] rounded"><Copy size={16}/></button>
          <button onClick={() => copyAsMarkdown(asMarkdown.customItem, { title: displayTitle, subtitle: meta, url, body: description, date: values.date }, "Copied as Markdown")}
            title="copy as Markdown" data-testid={`copy-md-custom-${item.id}`}
            className="p-1.5 hover:bg-[var(--pastel-mint)] rounded"><MarkdownLogo size={16}/></button>
          <button onClick={() => onCollapseToggle?.(!collapsed)}
            className="p-1.5 hover:bg-[var(--pastel-yellow)] rounded"
            title={collapsed ? "expand" : "collapse"}>
            {collapsed ? <CaretDown size={16}/> : <CaretUp size={16}/>}
          </button>
          <button onClick={onDelete} data-testid={`delete-custom-${item.id}`}
            className="p-1.5 hover:bg-[var(--pastel-peach)] rounded"><Trash size={16}/></button>
        </div>
      </div>

      {/* Expanded body — inline schema-driven fields */}
      {!collapsed && (
        <div className="border-t-[1.5px] border-[var(--brand-ink)] p-4 bg-[var(--pastel-yellow)]"
          data-testid={`editor-custom-${item.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sc.fields.map((f) => {
              const wrapCls = f.type === "textarea" ? "md:col-span-2"
                            : f.half ? ""
                            : "md:col-span-2";
              if (f.type === "checkbox") {
                return (
                  <label key={f.key}
                    className="flex items-center gap-2 text-sm md:col-span-2 py-1"
                    data-testid={`custom-field-${f.key}`}>
                    <input type="checkbox"
                      checked={!!values[f.key]}
                      onChange={(e) => setField(f.key, e.target.checked)}
                      className="h-4 w-4"/>
                    <span>{f.label}</span>
                  </label>
                );
              }
              if (f.type === "select") {
                return (
                  <div key={f.key} className={wrapCls}>
                    <label className="label-tiny block mb-1.5">
                      {f.label}{f.required && " *"}
                    </label>
                    <select value={values[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      data-testid={`custom-field-${f.key}`}
                      className="w-full card-hard rounded-md px-3 py-2 bg-white">
                      <option value="">— pick one —</option>
                      {f.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                );
              }
              if (f.type === "textarea") {
                return (
                  <div key={f.key} className={wrapCls}>
                    <label className="label-tiny block mb-1.5">
                      {f.label}{f.required && " *"}
                    </label>
                    <textarea value={values[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      placeholder={f.placeholder || ""}
                      data-testid={`custom-field-${f.key}`}
                      className="w-full card-hard rounded-md px-3 py-2 min-h-[80px] bg-white"/>
                  </div>
                );
              }
              return (
                <div key={f.key} className={wrapCls}>
                  <label className="label-tiny block mb-1.5">
                    {f.label}{f.required && " *"}
                  </label>
                  <input type={f.type === "url" ? "url" : "text"}
                    value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    data-testid={`custom-field-${f.key}`}
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex gap-2 items-center">
            <button onClick={lockIn} data-testid={`lock-in-custom-${item.id}`}
              disabled={saving}
              className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-1.5 hover:bg-[var(--brand-primary)]">
              <LockKey size={14} weight="bold"/> {saving ? "locking…" : "lock it in"}
            </button>
            <span className="text-xs text-[var(--brand-muted)]">
              auto-saves as you type · hit <b>lock it in</b> when you&apos;re done
            </span>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------- A whole custom section ----------
function CustomSection({ section, items, onChange }) {
  const kind = section.kind || "custom";
  const sc = schemaFor(kind);
  const [collapsed, setCollapsed] = useState(() => new Set(items.map((it) => it.id)));

  // Keep collapsed set in sync with item ids on load / delete.
  useEffect(() => {
    setCollapsed((cur) => {
      const next = new Set();
      items.forEach((it) => { if (cur.has(it.id)) next.add(it.id); });
      return next;
    });
    // Initial mount: land collapsed.
    // We can't easily detect first mount vs update — but only new items should
    // be expanded, and `create()` handles that below by explicitly removing
    // the new id from collapsed.
  }, [items.length]);

  const create = async () => {
    try {
      const payload = toApiPayload(extractFormValues({}, kind), kind, section.id);
      // Set a sensible starting title so the collapsed summary isn't blank.
      payload.title = `New ${sc.label.toLowerCase()}`;
      const { data } = await api.post("/custom/items", payload);
      // Ensure the new item lands expanded.
      setCollapsed((cur) => {
        const next = new Set(cur);
        next.delete(data.id);
        return next;
      });
      toast.success(`New ${sc.label.toLowerCase()} added`);
      onChange?.();
    } catch { toast.error("Couldn't add"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/custom/items/${id}`);
    onChange?.();
  };
  const removeSection = async () => {
    if (!confirm(`Delete the entire "${section.name}" section and its ${items.length} item${items.length === 1 ? "" : "s"}?`)) return;
    await api.delete(`/custom/sections/${section.id}`);
    toast.success("Section removed");
    onChange?.();
  };
  const clearAllItems = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Delete all ${items.length} item${items.length === 1 ? "" : "s"} in "${section.name}"? This can't be undone.`)) return;
    try {
      const { data } = await api.delete(`/custom/sections/${section.id}/items`);
      toast.success(`Wiped ${data.deleted} item${data.deleted === 1 ? "" : "s"}`);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear");
    }
  };

  const allCollapsed = items.length > 0 && items.every((e) => collapsed.has(e.id));
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(items.map((e) => e.id)));

  const setCollapse = (id, should) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (should) next.add(id); else next.delete(id);
      return next;
    });
  };

  return (
    <section className="card-hard rounded-md bg-white" data-testid={`section-custom-${section.id}`}>
      <div className="flex items-center justify-between p-5 border-b-[1.5px] border-[var(--brand-ink)] bg-white">
        <div>
          <p className="label-tiny">/{kind}</p>
          <h2 className="font-display text-2xl font-semibold tracking-tight">{section.name}</h2>
          {section.hint && <p className="text-xs text-[var(--brand-ink)]/60 mt-1">{section.hint}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono-d text-xs text-[var(--brand-muted)] mr-1">
            {items.length} {items.length === 1 ? "entry" : "entries"}
          </span>
          {items.length > 1 && (
            <button onClick={allCollapsed ? expandAll : collapseAll}
              data-testid={`custom-collapse-all-${section.id}`}
              className="btn-hard bg-white rounded-md px-3 py-2 text-xs flex items-center gap-1">
              {allCollapsed ? <CaretDown size={12} weight="bold"/> : <CaretUp size={12} weight="bold"/>}
              {allCollapsed ? "expand all" : "collapse all"}
            </button>
          )}
          {items.length > 0 && (
            <button onClick={clearAllItems} data-testid={`clear-all-custom-${section.id}`}
              title={`wipe all ${items.length} items in this section`}
              className="btn-hard bg-[#B91C1C] text-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5">
              <TrashSimple size={13} weight="bold"/>
              <span className="hidden sm:inline">delete all</span>
            </button>
          )}
          <button onClick={removeSection} title="delete section" data-testid={`remove-section-${section.id}`}
            className="btn-hard bg-white rounded-md p-2"><Trash size={14}/></button>
          <button onClick={create} data-testid={`add-custom-${section.id}`}
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-2 text-sm flex items-center gap-1.5 hover:bg-[var(--brand-ink)]">
            <Plus weight="bold" size={14}/> add {sc.label.toLowerCase()}
          </button>
        </div>
      </div>
      <div className="p-5">
        {items.length === 0 ? (
          <p className="text-sm text-[var(--brand-muted)]">no items yet.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((it) => (
              <ItemCard key={it.id} sectionId={section.id} kind={kind} item={it}
                onChange={() => onChange?.()}
                onDelete={() => remove(it.id)}
                collapsed={collapsed.has(it.id)}
                onCollapseToggle={(should) => setCollapse(it.id, should)}/>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------- Wrapper ----------
export default function CustomSections() {
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [picker, setPicker] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customHint, setCustomHint] = useState("");

  const load = async () => {
    const [s, i] = await Promise.all([api.get("/custom/sections"), api.get("/custom/items")]);
    setSections(s.data);
    setItems(i.data);
  };
  useEffect(() => { load(); }, []);

  const createSection = async (name, kind = "custom", hint = "") => {
    if (!name?.trim()) return;
    try {
      await api.post("/custom/sections", { name: name.trim(), kind, hint });
      toast.success("Section added");
      setPicker(false);
      setCustomName("");
      setCustomHint("");
      load();
    } catch { toast.error("Couldn't add"); }
  };

  return (
    <div className="space-y-8">
      {sections.map((s) => (
        <CustomSection key={s.id} section={s}
          items={items.filter((it) => it.section_id === s.id)}
          onChange={load}/>
      ))}

      <div className="card-hard rounded-md bg-[var(--pastel-yellow)] p-6" data-testid="add-custom-section">
        <p className="label-tiny mb-2"><FolderPlus size={12} className="inline mr-1"/> /add custom section</p>
        <h3 className="font-display text-2xl font-semibold tracking-tight">need something we didn&apos;t think of?</h3>
        <p className="text-sm text-[var(--brand-muted)] mt-2 mb-4">
          add your own section — portfolios, certificates, registrations, awards, whatever your applications ask for.
          each type has a tailored form so you&apos;re never filling in irrelevant fields.
        </p>
        {!picker ? (
          <button onClick={() => setPicker(true)} data-testid="open-custom-picker"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[var(--brand-ink)]">
            <Plus weight="bold" size={14}/> add a section
          </button>
        ) : (
          <div>
            <p className="label-tiny mb-3">/quick start — tap one (each has its own tailored form)</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
              {PROMPTS.map((p) => {
                const Icon = p.icon;
                return (
                  <button key={p.name} onClick={() => createSection(p.name, p.kind, p.hint)}
                    data-testid={`prompt-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                    title={p.hint}
                    className="btn-hard bg-white rounded-md p-3 text-left hover:bg-[var(--pastel-mint)]">
                    <Icon size={20} weight="duotone"/>
                    <p className="text-sm font-medium mt-1">{p.name}</p>
                    <p className="text-[10px] text-[var(--brand-muted)] mt-0.5 font-mono-d">/{p.kind}</p>
                  </button>
                );
              })}
            </div>
            <p className="label-tiny mb-3">/or roll your own (generic free-form)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input value={customName} onChange={(e) => setCustomName(e.target.value)}
                placeholder="Section name *"
                data-testid="custom-section-name"
                className="card-hard rounded-md px-3 py-2 bg-white"/>
              <input value={customHint} onChange={(e) => setCustomHint(e.target.value)}
                placeholder="optional hint shown above the section"
                className="card-hard rounded-md px-3 py-2 bg-white md:col-span-2"/>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => createSection(customName, "custom", customHint)}
                data-testid="custom-section-create"
                className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm hover:bg-[var(--brand-primary)]">
                create section
              </button>
              <button onClick={() => { setPicker(false); setCustomName(""); setCustomHint(""); }}
                className="btn-hard bg-white rounded-md px-4 py-2 text-sm">cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
